import { useNavigate } from 'react-router-dom';
import { useAuth } from './utils_js/useAuth';

function Home({ theme, toggleTheme, setIsDemo, showNotification }) {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();

  const handleDemoAccess = () => {
    navigate('/demo-selection');
  };

  const API_URL = import.meta.env.VITE_API_URL || '';

  const handleUserAccess = async () => {
    if (isLoading) {
      showNotification("Vérification en cours...", "info");
      return;
    }

    // On force une validation serveur AVANT de naviguer
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification("Vous devez être connecté.", "error");
        return;
      }

      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        localStorage.removeItem('token');
        showNotification("Session invalide. Veuillez vous reconnecter.", "error");
        return;
      }

      // On récupère les données utilisateur
      const data = await res.json();
      const userEmail = data.email || 'utilisateur';

      // Notification de bienvenue personnalisée
      showNotification(
        `Bienvenue ${userEmail.split('@')[0]} ! Vous êtes connecté.`,
        "success"
      );

      // Si OK → on peut y aller
      setIsDemo(false);
      navigate('/dashboard');

    } catch (err) {
      localStorage.removeItem('token');
      showNotification("Erreur de connexion. Veuillez vous reconnecter.", "error");
    }
  };

  // Base names des screenshots (sans l'extension ni le suffixe _clair/_sombre)
  const screenshotBases = [
    'portfolioOverview',
    'comparaisonBenchmark',
    'perfIndividuelle',
    'dca'
  ];

  const suffix = theme === 'light' ? '_clair' : '_sombre';

  const screenshots = screenshotBases.map(base => ({
    src: `/images/${base}${suffix}.png`,
    alt: base === 'portfolioOverview' ? 'Vue d’ensemble du portefeuille' :
         base === 'comparaisonBenchmark' ? 'Comparaison avec benchmarks' :
         base === 'perfIndividuelle' ? 'Performance individuelle' :
         'Analyse DCA',
    caption: base === 'portfolioOverview' ? 'Vue d’ensemble : Visualisez la performance globale de votre portefeuille.' :
             base === 'comparaisonBenchmark' ? 'Performance individuelle : Analysez vos positions et leur performance' :
             base === 'perfIndividuelle' ? 'Evolution des comptes : Comparez les résultats de vos comptes' :
             'Analyse DCA : Évaluez l’efficacité de votre stratégie d’investissement progressif.'
  }));

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <header className="flex justify-between items-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 dark:text-white">Suivi de Portefeuille</h1>
          <button
            onClick={toggleTheme}
            className="px-5 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-md"
          >
            {theme === 'light' ? 'Mode Sombre' : 'Mode Clair'}
          </button>
        </header>

        {/* Hero Section */}
        <section className="text-center mb-20">
          <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white pt-10 mb-6 leading-tight">
            Une plateforme simple et puissante pour suivre vos investissements.
          </h2>
          
          <p className="text-lg font-medium text-gray-800 dark:text-gray-200 max-w-6xl mx-auto mb-4">
            Vos données sont aussi claires et transparentes qu’un fichier Excel : historique 100 % traçable, import et export à tout moment. <br/>
            <strong>Des analyses riches, pertinentes et très bien organisées</strong> pour suivre la performance de votre portefeuille sur le long terme.
          </p>

          {/* Phrase "à l'écoute" en plus discret */}
          <p className="text-base italic text-gray-600 dark:text-gray-400 mb-10">
            Vous avez une idée de métrique, un graphique qui vous manque, une amélioration à proposer ?  
            Dites-le-nous : on construit le dashboard parfait ensemble.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transform hover:scale-105 transition-all shadow-lg"
            >
              Se connecter
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="px-6 py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transform hover:scale-105 transition-all shadow-lg"
            >
              Créer un compte
            </button>
            <button
              onClick={handleUserAccess}
              className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transform hover:scale-105 transition-all shadow-lg"
            >
              Ma base de données
            </button>
            <button
              onClick={handleDemoAccess}
              className="px-6 py-3 bg-gray-700 text-white font-semibold rounded-xl hover:bg-gray-800 transform hover:scale-105 transition-all shadow-lg"
            >
              Version Démo
            </button>
          </div>
        </section>

        {/* Screenshots Section */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-10 text-gray-900 dark:text-white">
            Découvrez Nos Tableaux de Bord
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {screenshots.map((shot, index) => (
              <div
                key={index}
                className="group bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 border border-gray-400 dark:border-gray-500"
              >
                <div className="overflow-hidden">
                  <img
                    src={shot.src}
                    alt={shot.alt}
                    className="w-full p-1 h-auto object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <p className="text-gray-700 dark:text-gray-300 text-center font-medium">
                    {shot.caption}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <footer className="border-t border-gray-300 dark:border-gray-700 pt-10 mt-20">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-8">
            {/* Colonne Mentions Légales */}
            <div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Mentions Légales</h3>
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                  {/* <p><strong>Éditeur du site :</strong> Nicolas Malet</p> */}
                  <p><strong>Contact :</strong> <a className="underline hover:text-blue-600 dark:hover:text-blue-400 transition" href="mailto:contact@suivi-de-portefeuille.fr">contact@suivi-de-portefeuille.fr</a></p>
                  <p><strong>Hébergeur :</strong> Vercel Inc., 340 S Lemon Ave #4133, Walnut, CA 91789, États-Unis</p>
                  <p>Conformément au RGPD, vos données sont protégées.                   
                  <button
                    onClick={() => navigate('/politique-confidentialite')}
                    className="px-1 underline hover:text-blue-600 dark:hover:text-blue-400 transition"
                  >
                    <p> Consultez notre Politique de confidentialité</p>
                  </button></p>
                </div>
              </div>  
            </div>

            {/* Colonne Liens utiles */}
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Liens utiles</h3>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => navigate('/documentation')}
                    className="underline hover:text-blue-600 dark:hover:text-blue-400 transition"
                  >
                    📚 Documentation & Guide
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/faq')}  // Ancre vers la FAQ
                    className="underline hover:text-blue-600 dark:hover:text-blue-400 transition"
                  >
                    ❓ FAQ & Aide
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/vision')}
                    className="underline hover:text-blue-600 dark:hover:text-blue-400 transition"
                  >
                    🌱 Notre vision & approche
                  </button>
                </li>
                {/* Tu pourras ajouter d’autres liens plus tard : Contact, CGU, etc. */}
              </ul>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            © {new Date().getFullYear()} suivi-de-portefeuille.fr. Tous droits réservés.
          </p>
        </footer>
      </div>
    </div>
  );
}

export default Home;